import { toUtcISOString } from './callJourneyTimeFilter';
import { convertLocalTimezone } from './dateUtils';

const splitValueColumns = new Set(['callQueue', 'participants']);

const toPrimitiveString = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
};

export const toFilterTokens = (columnKey: string, value: unknown): string[] => {
  if (value === null || value === undefined) {
    return [];
  }

  if (columnKey === 'voicemail') {
    if (value === true || value === 'true') return ['Yes'];
    if (value === false || value === 'false') return ['No'];
  }

  const raw = toPrimitiveString(value).trim();
  if (!raw) {
    return [];
  }

  if (columnKey === 'startTime') {
    try {
      return [toUtcISOString(raw)];
    } catch {
      return [raw];
    }
  }

  if (splitValueColumns.has(columnKey)) {
    return raw.split(',').map(part => part.trim()).filter(Boolean);
  }

  return [raw];
};

export const normalizeColumnValues = (columnKey: string, values: unknown[]): string[] => {
  const uniqueValues = new Set<string>();
  values.forEach(value => {
    toFilterTokens(columnKey, value).forEach(token => uniqueValues.add(token));
  });
  return Array.from(uniqueValues);
};

export const formatStartTimeDisplayValue = (value: unknown): string => {
  const raw = toPrimitiveString(value).trim();
  if (!raw) {
    return '';
  }

  const formatDateToLocalString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const dotNetEpochRegex = /^\/Date\((\d+)\)\/$/;
  const dotNetEpochMatch = dotNetEpochRegex.exec(raw);
  if (dotNetEpochMatch) {
    const epochDate = new Date(Number(dotNetEpochMatch[1]));
    if (!Number.isNaN(epochDate.getTime())) {
      return formatDateToLocalString(epochDate);
    }
  }

  if (/^\d{10,13}$/.test(raw)) {
    const asNumber = Number(raw.length === 10 ? `${raw}000` : raw);
    const epochDate = new Date(asNumber);
    if (!Number.isNaN(epochDate.getTime())) {
      return formatDateToLocalString(epochDate);
    }
  }

  const normalizeForUtcParse = (input: string): string => {
    let normalized = input.replace(' ', 'T');
    normalized = normalized.replace(/\.(\d{3})\d+/, '.$1');

    if (!/(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(normalized)) {
      normalized = `${normalized}Z`;
    }

    return normalized;
  };

  const normalizedDate = new Date(normalizeForUtcParse(raw));
  if (!Number.isNaN(normalizedDate.getTime())) {
    return formatDateToLocalString(normalizedDate);
  }

  const local = convertLocalTimezone(raw);
  return local || raw;
};
